import {
  Box,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  ButtonGroup,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import GridViewIcon from "@mui/icons-material/GridView";
import TableViewIcon from "@mui/icons-material/TableView";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useMemo } from "react";

import { usePrizes } from "../../hooks/usePrizes";
import { useLotteries } from "../../hooks/useLotteries";
import ChristmasLoader from "../../Components/ChristmasLoader";
import { PrizeCard } from "./PrizeCard";
import { PrizeTable } from "./PrizeTable";
import { EmptyPrizes } from "./EmptyPrizes";
import { PrizeDto } from "../../models/Lottery";

export const PrizesContainer = () => {
  const { lotteries, loading: lLoading } = useLotteries();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-driven state
  const selectedLotteryId =
    searchParams.get("lotteryId") ?? lotteries[0]?.sessionId ?? "";

  const viewType = (searchParams.get("view") as "tiles" | "table") ?? "table";

  const search = searchParams.get("search") ?? "";

  const { lotteryPrizes, loading } = usePrizes(selectedLotteryId);

  // Initialize lotteryId if missing
  useEffect(() => {
    if (!searchParams.get("lotteryId") && lotteries.length > 0) {
      setSearchParams((prev) => {
        prev.set("lotteryId", lotteries[0].sessionId ?? "");
        return prev;
      });
    }
  }, [lotteries, searchParams, setSearchParams]);

  const filteredPrizes = useMemo(() => {
    if (!lotteryPrizes) return [];

    const term = search.trim().toLowerCase();
    if (!term) return lotteryPrizes.prizes;

    return lotteryPrizes.prizes.filter((p: PrizeDto) => {
      const name = p.name?.toLowerCase() ?? "";
      const description = p.description?.toLowerCase() ?? "";
      return name.includes(term) || description.includes(term);
    });
  }, [lotteryPrizes, search]);

  if (loading || lLoading) return <ChristmasLoader />;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "hsl(30, 50%, 98%)", py: 4 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontFamily: '"Mountains of Christmas", cursive',
              color: "hsl(150, 60%, 25%)",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            🎁 Δώρα
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <ButtonGroup variant="outlined">
              <IconButton
                onClick={() =>
                  setSearchParams((prev) => {
                    prev.set("view", "tiles");
                    return prev;
                  })
                }
              >
                <GridViewIcon />
              </IconButton>

              <IconButton
                onClick={() =>
                  setSearchParams((prev) => {
                    prev.set("view", "table");
                    return prev;
                  })
                }
              >
                <TableViewIcon />
              </IconButton>
            </ButtonGroup>

            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel>Εμφάνιση ανα λοταρία</InputLabel>
              <Select
                value={selectedLotteryId}
                label="Εμφάνιση ανα λοταρία"
                onChange={(e) =>
                  setSearchParams((prev) => {
                    prev.set("lotteryId", e.target.value);
                    return prev;
                  })
                }
              >
                {lotteries.map((lottery) => (
                  <MenuItem
                    key={lottery.sessionId}
                    value={lottery.sessionId as string}
                  >
                    {lottery.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              value={search}
              placeholder="Αναζήτηση δώρου..."
              onChange={(e) =>
                setSearchParams((prev) => {
                  const value = e.target.value;
                  value ? prev.set("search", value) : prev.delete("search");
                  return prev;
                })
              }
              sx={{ minWidth: 220, bgcolor: "white", borderRadius: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              component={Link}
              to="/prizes/new"
              variant="contained"
              startIcon={<AddIcon />}
            >
              προσθηκη δωρου
            </Button>
          </Box>
        </Box>

        {!lotteryPrizes || lotteryPrizes.prizes.length === 0 ? (
          <EmptyPrizes />
        ) : filteredPrizes.length === 0 ? (
          <Typography color="error">
            Δεν βρέθηκαν δώρα για την αναζήτηση "{search}"
          </Typography>
        ) : viewType === "table" ? (
          <PrizeTable
            prizes={filteredPrizes}
            lotteryTitle={lotteryPrizes.title}
          />
        ) : (
          <Grid container spacing={3}>
            {filteredPrizes.map((prize) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={prize.id}>
                <PrizeCard prize={prize} lotteryTitle={lotteryPrizes.title} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};
